import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Modal from './Modal';
import api from '../services/api';

// Các lỗi 400 mà backend trả về khi người được chọn không đủ điều kiện nhận ca đo
const ASSIGN_ERROR_MESSAGES = {
  'A video session may only be assigned to an active specialist; ask a super admin to grant specialist status first':
    'Người này không phải chuyên viên đang hoạt động. Liên hệ super admin để cấp tư cách chuyên viên trước.',
  "That specialist belongs to a different organization than the session's subject":
    'Chuyên viên này thuộc tổ chức khác với khách hàng của ca đo.',
};

function VideoSessionAssignModal({ session, onClose }) {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownRect, setDropdownRect] = useState(null);

  const searchBoxRef = useRef(null);
  const dropdownRef = useRef(null);

  const fetchShares = async () => {
    setLoading(true);
    try {
      const response = await api.getVideoSessionShares(session.id, 1, 100);
      setShares(response.items || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSpecialists = async () => {
    setSearching(true);
    try {
      const response = await api.getAssignableSpecialists(session.id, 1, 20, query.trim());
      setSearchResults(response.items || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    fetchShares();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.id]);

  useEffect(() => {
    if (!isDropdownOpen) return;

    const timer = setTimeout(fetchSpecialists, query.trim() ? 300 : 0);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, isDropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const clickedSearchBox = searchBoxRef.current && searchBoxRef.current.contains(e.target);
      const clickedDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!clickedSearchBox && !clickedDropdown) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openDropdown = () => {
    if (searchBoxRef.current) {
      const rect = searchBoxRef.current.getBoundingClientRect();
      setDropdownRect({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    setIsDropdownOpen(true);
  };

  const isAlreadyShared = (specialist) =>
    specialist.already_shared || shares.some((share) => share.shared_with_user_id === specialist.user_id);

  const handleAssign = async (specialist) => {
    if (isAlreadyShared(specialist)) return;

    setError(null);
    try {
      // Backend nhận user_id của tài khoản, không phải specialist_id
      await api.createVideoSessionShare(session.id, specialist.user_id);
      setQuery('');
      fetchShares();
      fetchSpecialists();
    } catch (err) {
      if (err.status === 409) {
        setError('Chuyên viên này đã được assign session này rồi.');
      } else {
        setError(ASSIGN_ERROR_MESSAGES[err.message] || err.message);
      }
    }
  };

  const handleUnassign = async (share) => {
    if (!window.confirm(`Bỏ assign "${share.shared_with_username}" khỏi session này?`)) {
      return;
    }
    setError(null);
    try {
      await api.deleteVideoSessionShare(session.id, share.id);
      fetchShares();
      if (isDropdownOpen) fetchSpecialists();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Modal title={`Assign session #${session.id}`} onClose={onClose}>
      {error && <div className="error-message">{error}</div>}

      <div className="form-group">
        <label>Đang được chia sẻ với</label>
        {loading ? (
          <p>Loading...</p>
        ) : shares.length === 0 ? (
          <p>Chưa assign cho ai.</p>
        ) : (
          <ul className="share-list">
            {shares.map((share) => (
              <li key={share.id} className="share-item">
                <div className="share-item-info">
                  <span>
                    {share.shared_with_full_name || share.shared_with_username}
                    {share.shared_with_full_name ? ` (${share.shared_with_username})` : ''}
                  </span>
                  {share.shared_with_is_active_specialist === false && (
                    <span className="share-item-warning">
                      Đã bị thu hồi tư cách chuyên viên — không còn xem được ca đo này. Nên gỡ khỏi
                      danh sách.
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="share-item-remove"
                  aria-label="Unassign"
                  onClick={() => handleUnassign(share)}
                >
                  &times;
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="form-group">
        <label>Assign cho chuyên viên khác</label>
        <div className="assign-search" ref={searchBoxRef}>
          <input
            type="text"
            placeholder="Tìm theo tên/username/chức danh..."
            value={query}
            onFocus={openDropdown}
            onChange={(e) => {
              setQuery(e.target.value);
              openDropdown();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setIsDropdownOpen(false);
            }}
          />
          <span className={`assign-search-caret${isDropdownOpen ? ' is-open' : ''}`}>
            &#9662;
          </span>
        </div>
      </div>

      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          Đóng
        </button>
      </div>

      {isDropdownOpen &&
        dropdownRect &&
        createPortal(
          <ul
            ref={dropdownRef}
            className="assign-dropdown"
            style={{ top: dropdownRect.top, left: dropdownRect.left, width: dropdownRect.width }}
          >
            {searching ? (
              <li className="assign-dropdown-empty">Đang tìm...</li>
            ) : searchResults.length === 0 ? (
              <li className="assign-dropdown-empty">Không tìm thấy chuyên viên</li>
            ) : (
              searchResults.map((specialist) => {
                const alreadyShared = isAlreadyShared(specialist);
                return (
                  <li
                    key={specialist.specialist_id}
                    className={`assign-dropdown-item${alreadyShared ? ' is-disabled' : ''}`}
                    onClick={() => handleAssign(specialist)}
                  >
                    <div className="assign-dropdown-item-info">
                      <span>
                        {specialist.full_name || specialist.username}
                        {specialist.full_name ? ` (${specialist.username})` : ''}
                      </span>
                      {specialist.title && (
                        <span className="assign-dropdown-subtitle">{specialist.title}</span>
                      )}
                    </div>
                    {alreadyShared && <span className="assign-dropdown-tag">Đã assign</span>}
                  </li>
                );
              })
            )}
          </ul>,
          document.body
        )}
    </Modal>
  );
}

export default VideoSessionAssignModal;
